import { Capacitor } from '@capacitor/core';

// Production Verified Shared Backend URL for Web & Mobile App (Cloud Run asia-southeast1)
export const PRODUCTION_API_URL = 'https://new-pairup-xni7o4bhcq-as.a.run.app';

// Development Backend URL for local/testing
export const DEVELOPMENT_API_URL = 'https://new-pairup-xni7o4bhcq-as.a.run.app';

/**
 * Returns true if running natively inside an Android or iOS Capacitor shell
 */
export function isNativeApp(): boolean {
  return Capacitor.isNativePlatform();
}

/**
 * Returns the current platform name: 'web' | 'android' | 'ios'
 */
export function getAppPlatform(): 'web' | 'android' | 'ios' {
  const platform = Capacitor.getPlatform();
  if (platform === 'android') return 'android';
  if (platform === 'ios') return 'ios';
  return 'web';
}

/**
 * Returns true if the candidate URL points to localhost or loopback.
 */
export function isLocalhostUrl(url?: string | null): boolean {
  if (!url || typeof url !== 'string') return false;
  return /^https?:\/\/(localhost|127\.0\.0\.1|0\.0\.0\.0)(:\d+)?(\/|$)/i.test(url.trim());
}

/**
 * Resolves the base API URL:
 * - If VITE_API_BASE_URL is explicitly configured, uses it across all runtimes
 *   EXCEPT in production builds (PROD === true) where localhost/loopback values
 *   from local .env files are rejected to prevent baking localhost into dist/mobile bundles.
 * - On Web: returns '' (empty string) to preserve standard relative /api/... calls
 * - On Native Android & iOS: returns the configured hosted backend URL
 */
export function getApiBaseUrl(overrideEnv?: { VITE_API_BASE_URL?: string; PROD?: boolean }): string {
  if (
    !overrideEnv &&
    !isNativeApp() &&
    typeof window !== 'undefined' &&
    isLocalhostUrl(window.location.origin)
  ) {
    return '';
  }

  const metaEnv = overrideEnv ?? ((import.meta as any).env || {});
  const envUrl = metaEnv?.VITE_API_BASE_URL;
  const isProd = Boolean(metaEnv?.PROD);

  if (envUrl && typeof envUrl === 'string' && envUrl.trim().length > 0) {
    const cleaned = envUrl.trim().replace(/\/+$/, '');
    if (!(isProd && isLocalhostUrl(cleaned))) {
      return cleaned;
    }
  }

  if (!isNativeApp()) {
    return '';
  }

  // Fallback to verified production deployment URL
  return PRODUCTION_API_URL;
}

/**
 * Resolves a full URL for any API endpoint
 * @param path Endpoint path, e.g. '/api/v1/bookings'
 */
export function resolveApiUrl(path: string): string {
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }

  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const baseUrl = getApiBaseUrl();

  if (!baseUrl) {
    return normalizedPath;
  }

  return `${baseUrl}${normalizedPath}`;
}

/**
 * Resolves the WebSocket endpoint for real-time notifications & chat:
 * - On Web: uses window.location.host
 * - On Native Android/iOS: derives wss:// URL from the hosted API URL
 */
export function getWebSocketUrl(): string {
  if (isNativeApp()) {
    const baseUrl = getApiBaseUrl();
    const wsUrl = baseUrl.replace(/^https:\/\//i, 'wss://').replace(/^http:\/\//i, 'ws://');
    return `${wsUrl}/ws`;
  }

  if (typeof window !== 'undefined') {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${window.location.host}/ws`;
  }

  return 'wss://new-pairup-xni7o4bhcq-as.a.run.app/ws';
}

/**
 * Universal fetch wrapper that automatically resolves API URLs on native mobile
 * and falls back to the authoritative Cloud Run backend if a static web host
 * returns HTML instead of JSON for an /api/* request.
 */
export async function apiFetch(
  input: string | URL | Request,
  init?: RequestInit
): Promise<Response> {
  let targetUrl: string | URL | Request = input;
  let normalizedRelativeApiPath: string | null = null;

  if (typeof input === 'string') {
    if (!input.startsWith('http://') && !input.startsWith('https://')) {
      const cleanPath = input.startsWith('/') ? input : `/${input}`;
      if (cleanPath.startsWith('/api/')) {
        normalizedRelativeApiPath = cleanPath;
      }
    }
    targetUrl = resolveApiUrl(input);
  }

  const response = await fetch(targetUrl, init);

  if (
    normalizedRelativeApiPath &&
    typeof targetUrl === 'string' &&
    targetUrl.startsWith('/api/')
  ) {
    const contentType = (response.headers.get('content-type') || '').toLowerCase();
    if (contentType.includes('text/html')) {
      return fetch(`${PRODUCTION_API_URL}${normalizedRelativeApiPath}`, init);
    }
  }

  return response;
}
